/**
 * Document content templates for generated proposals / contracts.
 *
 * Each service type maps to a "family" that defines the document title, cover
 * letter, ordered sections (with default boilerplate text), and default
 * Work Specifications + document_data values. Boilerplate uses {{token}}
 * placeholders that the renderer fills from the record + document_data.
 *
 * Everything here is a DEFAULT: the proposal/contract form prefills these into
 * the editable `document_data`, so any wording can be changed per document.
 */
import type {
  DocumentData,
  DocumentSectionOverride,
  WorkSpecRow,
} from '../types/agreement'

export const RBC_BRANDING = {
  companyName: 'RBC Facility Services',
  companyAddress: '670 Myrtle Ave #7348, Brooklyn, NY 11205',
  companyAddressLines: ['670 Myrtle Ave #7348', 'Brooklyn, NY 11205'],
  repName: 'Abe Spitz',
  repPhone: '718-427-2111',
  repEmail: 'Abe@rbcfs.com',
  website: 'app.rbcfs.com',
}

export type DocumentSectionKind = 'text' | 'compensation' | 'workspecs' | 'signature'

export interface DocumentSectionTemplate {
  id: string
  heading: string
  kind: DocumentSectionKind
  /** Boilerplate body for `text` sections, or a lead-in line for compensation/signature. */
  defaultText?: string
}

export interface DocumentTemplate {
  family: string
  documentTitle: string
  bodyTitle: string
  subject: string
  introParagraphs: string[]
  sections: DocumentSectionTemplate[]
  defaultWorkSpecs: WorkSpecRow[]
  defaultDocumentData: DocumentData
}

// ---------------------------------------------------------------------------
// Shared boilerplate (identical or near-identical across families)
// ---------------------------------------------------------------------------

const INSURANCE_TEXT =
  '{{companyName}} will furnish all forms of insurance required by law and shall maintain the same in force.\n• Comprehensive General Liability\n• Workers Compensation'

const EMPLOYEE_STATUS_TEXT =
  'Personnel supplied by {{companyName}} are deemed employees of {{companyName}} and will not for any purpose be considered employees or agents of the customer.'

const CANCELLATION_TEXT =
  'This agreement may be terminated or canceled at any time with a minimum of thirty (30) days written notice from either party.'

const REGULATORY_TEXT =
  '{{companyName}} is responsible for maintaining the property only during scheduled service hours. {{companyName}} shall not be liable for any fines or violations issued by the NYC Department of Sanitation (DSNY) or other authorities (e.g., "Dirty Sidewalk" or "Overflowing Containers") unless such violation is the direct result of our failure to perform services during a scheduled visit.\n\nThe Customer acknowledges that {{companyName}} is not responsible for conditions arising between scheduled visits, including but not limited to litter, tenant move-outs, or illegal dumping. The Customer agrees to indemnify {{companyName}} against all such claims or penalties.'

const SNOW_TEXT =
  '{{companyName}} will perform snow removal at the earliest possible opportunity following the conclusion of a snowfall event. We are not liable for damages, injuries, or claims resulting from snow or ice accumulations, or from any delay in service response.\n\n{{companyName}} is not responsible for conditions existing prior to our arrival or arising after a service visit is completed, including re-accumulation, drifting, or the refreezing of meltwater. Responsibility for site safety remains with the Client at all times outside of active service performance.\n\n{{snowClause}}'

const AGREEMENT_TEXT =
  'This Agreement ("this Agreement") is made and entered into as of {{agreementDate}} by and between {{companyName}}, with its principal place of business located at {{companyAddress}}, and {{customerCompany}} located at {{customerAddress}}.\n\nNOW, THEREFORE, in consideration of the mutual promises and benefits to be derived by the parties they mutually agree to the terms and conditions as outlined above in this agreement.\n\nIN WITNESS WHEREOF, the parties have executed this Agreement effective as of the date and year first written above.'

const PREVAILING_WAGE_COMPLIANCE_TEXT =
  'Compliance\n{{companyName}} acknowledges that the property is subject to NYC tax abatement requirements. All building service employees shall be paid not less than the prevailing wage and supplements as determined by the New York City Comptroller.\n\nPrice Adjustments\nIf compliance with prevailing wage requirements increases Contractor\u2019s labor costs (including wage/supplement rates, classification requirements, or changes in interpretation/enforcement), Contractor may adjust pricing upon written notice. Any such adjustment will be limited to the amount reasonably necessary to maintain compliance.\n\nIf Contractor reasonably determines it cannot perform the Services in compliance with applicable prevailing wage requirements due to Client\u2019s refusal to approve a required price adjustment or refusal to provide reasonably requested coverage documentation, Contractor may terminate the Agreement upon fifteen (15) days written notice. Payment remains due for Services performed through the termination date.'

// ---------------------------------------------------------------------------
// Default Work Specifications per family
// ---------------------------------------------------------------------------

let wsCounter = 0
function ws(group: string, task: string, frequency = ''): WorkSpecRow {
  wsCounter += 1
  return { id: `ws-${wsCounter}`, group, task, frequency }
}

const MAINTENANCE_WORK_SPECS: WorkSpecRow[] = [
  ws('Interior', 'Brush/Dust off mats', '1 day/week'),
  ws('Interior', 'Wash and shine doors, windows, glass, frame and sills', '1 day/week'),
  ws('Interior', 'Sweep and mop all floors', '1 day/week'),
  ws('Interior', 'Vacuum steps', '1 day/week'),
  ws('Interior', 'Wipe railings, ledges and window sills', '1 day/week'),
  ws('Interior', 'Wipe down all surfaces in the hallways and stairways', '1 day/week'),
  ws('Bulbs', 'Bulbs replaced', 'As needed'),
  ws('Exterior', 'Carry trash cans to curb (return cans following morning)', '3 days/week'),
  ws('Exterior', 'Carry recyclables to curb', '1 day/week'),
  ws('Exterior', 'Fold, organize and carry boxes to curb', '1 day/week'),
  ws('Exterior', 'Sweep front of building', '6 days/week'),
  ws('Exterior', 'Clean garbage area', '6 days/week'),
  ws('Snow Removal', 'Snow shoveled', 'As needed'),
  ws('Snow Removal', 'Area salted', 'As needed'),
]

const CLEANING_SPECS: WorkSpecRow[] = [
  ws('Cleaning Specifications', 'Labels and stickers not required to remain will be removed from new fixtures and appliances'),
  ws('Cleaning Specifications', 'Ceiling fans, floor and ceiling vents and return covers will be cleaned; sawdust and drywall dust removed from walls and ceilings'),
  ws('Cleaning Specifications', 'All windows will be cleaned inside, and where accessible outside'),
  ws('Cleaning Specifications', 'Interior of cabinets and drawers will be vacuumed to remove dust in corners'),
  ws('Cleaning Specifications', 'Woodwork including doors, frames, trim, cabinets, drawers, shelves, handrails and moldings will be wiped down'),
  ws('Cleaning Specifications', 'Horizontal surfaces including counter tops will be wiped clean'),
  ws('Cleaning Specifications', 'Mirrors, porcelain surfaces and chrome fixtures will be cleaned and polished'),
  ws('Cleaning Specifications', 'Window tracks will be vacuumed to remove construction dust'),
  ws('Cleaning Specifications', 'Baseboards, ledges, electrical outlet covers and light switches will be cleaned'),
  ws('Cleaning Specifications', 'Restroom fixtures including sinks, toilets, urinals, showers and tubs will be cleaned'),
  ws('Cleaning Specifications', 'Kitchen sink, counter tops, and exterior of appliances will be cleaned'),
  ws('Cleaning Specifications', 'Hard floors will be vacuumed, dust mopped or swept, then wet mopped'),
  ws('Cleaning Specifications', 'Carpeted areas including edges will be vacuumed using detail attachment'),
  ws('Standard Procedures', 'All necessary supervision, cleaning equipment and supplies will be furnished by cleaning company'),
  ws('Standard Procedures', 'Cleaning will be performed to a maximum height of 12 ft. above floor'),
  ws('Standard Procedures', 'When needed, safety cones or barriers will be used to limit access to the area being serviced'),
  ws('Standard Procedures', 'Cleaning company will be provided with all necessary security codes, fobs or access keys prior to start of work'),
  ws('Standard Procedures', 'Upon completion, all equipment, signs and used supplies will be removed and the building secured per instruction'),
]

const OFFICE_WORK_SPECS: WorkSpecRow[] = [
  ws('Interior', 'Empty all waste receptacles and replace liners', 'Each visit'),
  ws('Interior', 'Dust desks, surfaces and fixtures', 'Each visit'),
  ws('Interior', 'Vacuum carpeted areas', 'Each visit'),
  ws('Interior', 'Sweep and mop hard floors', 'Each visit'),
  ws('Interior', 'Clean and sanitize restrooms; restock supplies', 'Each visit'),
  ws('Interior', 'Wipe down kitchen / break room surfaces', 'Each visit'),
  ws('Interior', 'Spot-clean glass doors and partitions', 'Each visit'),
]

// ---------------------------------------------------------------------------
// Section builders per family
// ---------------------------------------------------------------------------

function maintenanceSections(opts: { dsny: boolean }): DocumentSectionTemplate[] {
  return [
    {
      id: 'general',
      heading: 'General',
      kind: 'text',
      defaultText:
        '{{companyName}} agrees to provide all labor, supervision, material, and equipment necessary to assure performance of specified building upkeep / janitorial services for the customer. This shall include all services described in the written specifications attached. {{companyName}} agrees to furnish such service for a period of one year, the dates yet to be agreed on.',
    },
    {
      id: 'compensation',
      heading: 'Compensation',
      kind: 'compensation',
      defaultText: 'Complete scope of work described in the attached Work Specifications along with detailed schedule.',
    },
    {
      id: 'special_services',
      heading: 'Special Services',
      kind: 'text',
      defaultText:
        'Carpet cleaning is available – price quoted upon request.\nWaxable hard surface floors can be stripped and refinished or scrubbed and re-waxed – price quoted upon request.\nExterior windows can be cleaned – price quoted upon request.\n\n{{additionalWorkClause}}',
    },
    {
      id: 'invoicing',
      heading: 'Invoicing',
      kind: 'text',
      defaultText: '{{invoicingClause}}',
    },
    {
      id: 'supervision',
      heading: 'Supervision',
      kind: 'text',
      defaultText: 'Adequate personnel and supervision will be furnished to ensure quality service.',
    },
    {
      id: 'supplies',
      heading: 'Supplies',
      kind: 'text',
      defaultText:
        '{{suppliesProvider}} will furnish all necessary cleaning supplies inclusive of, but not limited to: cleaning agents, disinfectants, trash liners, etc.',
    },
    {
      id: 'equipment',
      heading: 'Equipment',
      kind: 'text',
      defaultText:
        '{{equipmentProvider}} will furnish and maintain all necessary cleaning equipment inclusive of, but not limited to: vacuums, mop buckets, wringers, mops, and brooms.',
    },
    { id: 'snow', heading: 'Snow', kind: 'text', defaultText: SNOW_TEXT },
    ...(dsnyRegulatory(opts.dsny)),
    { id: 'insurance', heading: 'Insurance', kind: 'text', defaultText: INSURANCE_TEXT },
    { id: 'employee_status', heading: 'Employee Status', kind: 'text', defaultText: EMPLOYEE_STATUS_TEXT },
    {
      id: 'philosophy',
      heading: 'Our Philosophy',
      kind: 'text',
      defaultText:
        '{{companyName}} is committed to providing only the highest quality building upkeep / janitorial services that deliver the highest levels of customer satisfaction.',
    },
    { id: 'term', heading: 'Term', kind: 'text', defaultText: '{{termClause}}' },
    { id: 'cancellation', heading: 'Cancellation', kind: 'text', defaultText: CANCELLATION_TEXT },
    { id: 'agreement', heading: 'Agreement', kind: 'signature', defaultText: AGREEMENT_TEXT },
    { id: 'work_specifications', heading: 'Work Specifications', kind: 'workspecs' },
  ]
}

function dsnyRegulatory(dsny: boolean): DocumentSectionTemplate[] {
  // Regulatory Compliance / DSNY violations clause appears for maintenance + PW.
  return [{ id: 'regulatory', heading: 'Regulatory Compliance & Violations', kind: 'text', defaultText: REGULATORY_TEXT }]
}

function prevailingWageSections(opts: { dsny: boolean }): DocumentSectionTemplate[] {
  return [
    {
      id: 'general',
      heading: 'General',
      kind: 'text',
      defaultText:
        '{{companyName}} agrees to provide all labor, supervision, material, and equipment necessary to assure performance of specified building upkeep / janitorial services for the customer. This shall include all services described in the written specifications attached. {{companyName}} agrees to furnish such service for a period of one year, the dates yet to be agreed on.',
    },
    {
      id: 'prevailing_wage_compliance',
      heading: 'Prevailing Wage Compliance',
      kind: 'text',
      defaultText: PREVAILING_WAGE_COMPLIANCE_TEXT,
    },
    {
      id: 'compensation',
      heading: 'Compensation',
      kind: 'compensation',
      defaultText: 'Complete scope of work described in the attached Work Specifications along with detailed schedule.',
    },
    { id: 'invoicing', heading: 'Invoicing', kind: 'text', defaultText: '{{invoicingClause}}' },
    {
      id: 'supervision',
      heading: 'Supervision',
      kind: 'text',
      defaultText: 'Adequate personnel and supervision will be furnished to ensure quality service.',
    },
    {
      id: 'equipment',
      heading: 'Equipment',
      kind: 'text',
      defaultText:
        '{{equipmentProvider}} will furnish and maintain all necessary cleaning equipment inclusive of, but not limited to: vacuums, mop buckets, wringers, mops, and brooms.',
    },
    {
      id: 'supplies',
      heading: 'Supplies',
      kind: 'text',
      defaultText:
        '{{suppliesProvider}} will furnish all necessary cleaning supplies inclusive of, but not limited to: cleaning agents, disinfectants, trash liners, lightbulbs, salt for snow, mattress covers.',
    },
    { id: 'snow', heading: 'Snow', kind: 'text', defaultText: SNOW_TEXT },
    ...dsnyRegulatory(opts.dsny),
    { id: 'insurance', heading: 'Insurance', kind: 'text', defaultText: INSURANCE_TEXT },
    { id: 'employee_status', heading: 'Employee Status', kind: 'text', defaultText: EMPLOYEE_STATUS_TEXT },
    {
      id: 'philosophy',
      heading: 'Our Philosophy',
      kind: 'text',
      defaultText:
        '{{companyName}} is committed to providing only the highest quality building upkeep / janitorial services that deliver the highest levels of customer satisfaction.',
    },
    { id: 'term', heading: 'Term', kind: 'text', defaultText: '{{termClause}}' },
    { id: 'cancellation', heading: 'Cancellation', kind: 'text', defaultText: CANCELLATION_TEXT },
    { id: 'agreement', heading: 'Agreement', kind: 'signature', defaultText: AGREEMENT_TEXT },
    { id: 'work_specifications', heading: 'Work Specifications', kind: 'workspecs' },
  ]
}

function cleaningSections(opts: { workLabel: string }): DocumentSectionTemplate[] {
  const { workLabel } = opts
  return [
    {
      id: 'general',
      heading: 'General',
      kind: 'text',
      defaultText: `{{companyName}} agrees to provide all labor, supervision, material, and equipment necessary to assure performance of requested ${workLabel} service for the customer. This shall include all services described in the written specifications attached.`,
    },
    {
      id: 'compensation',
      heading: 'Compensation',
      kind: 'compensation',
      defaultText: `Complete ${workLabel} of all areas as described in the attached Work Specifications.`,
    },
    { id: 'insurance', heading: 'Insurance', kind: 'text', defaultText: INSURANCE_TEXT },
    {
      id: 'service_schedule',
      heading: 'Service Schedule',
      kind: 'text',
      defaultText:
        '{{scheduleClause}} The crew will observe holidays observed by the customer. {{companyName}} is prepared to adapt this work schedule to coincide with the needs and requests of the customer provided they are mutually agreed to, and do not alter the cost of operations.',
    },
    {
      id: 'additional_work',
      heading: 'Additional Work',
      kind: 'text',
      defaultText:
        'Customer may elect to have {{companyName}} provide additional labor in connection with its work. {{additionalWorkClause}}',
    },
    { id: 'invoicing', heading: 'Billing', kind: 'text', defaultText: '{{invoicingClause}}' },
    {
      id: 'supervision',
      heading: 'Supervision',
      kind: 'text',
      defaultText:
        'Delivering high quality service relies heavily on properly trained management and cleaning personnel. At {{companyName}}, both adequate personnel and supervision will be furnished to ensure the quality of our services.',
    },
    {
      id: 'supplies',
      heading: 'Supplies',
      kind: 'text',
      defaultText:
        '{{suppliesProvider}} will furnish all necessary cleaning supplies inclusive of, but not limited to: rags, towels, cleaning agents, disinfectants, scrapers, etc.',
    },
    {
      id: 'equipment',
      heading: 'Equipment',
      kind: 'text',
      defaultText:
        '{{equipmentProvider}} will furnish all necessary cleaning equipment inclusive of, but not limited to: shop vacs, mop buckets, wringers, applicators, ladders, mops and brooms, etc.',
    },
    { id: 'employee_status', heading: 'Employee Status', kind: 'text', defaultText: EMPLOYEE_STATUS_TEXT },
    {
      id: 'philosophy',
      heading: 'Our Philosophy',
      kind: 'text',
      defaultText:
        '{{companyName}} is committed to providing only the highest quality service. To deliver on this commitment, we bring together the best people, most effective methods, highest quality supplies, and latest equipment.',
    },
    { id: 'term', heading: 'Term', kind: 'text', defaultText: '{{termClause}}' },
    { id: 'cancellation', heading: 'Cancellation', kind: 'text', defaultText: CANCELLATION_TEXT },
    { id: 'agreement', heading: 'Agreement', kind: 'signature', defaultText: AGREEMENT_TEXT },
    { id: 'work_specifications', heading: 'Work Specifications', kind: 'workspecs' },
  ]
}

const MAINTENANCE_INTRO = [
  'Before we start service: All of our cleaners are thoroughly trained on how to perform each task effectively, as well as on important safety issues. Our goal is to clean each customer\u2019s building professionally and safely.',
  'Starting service: We know a seamless, no-hassle start-up is important to every customer. So at {{companyName}}, we combine up-front preparation and training with strong management and direction to ensure a smooth, successful startup.',
  'While we service: A systematic approach to keep your building looking good is what you\u2019re looking for. So, at {{companyName}}, we offer strong management and quality control to plan for, and not lose track of, the many necessary cleaning details.',
]

const CLEANING_INTRO = [
  'Before we start the project: All of our cleaners are thoroughly trained on how to perform each cleaning task, as well as on important safety issues. Our goal is to clean each customer\u2019s building professionally and safely.',
  'During the project: We know being able to count on dependable, worry-free service is important to every customer. So at {{companyName}}, we combine up-front preparation and training with strong management and direction to ensure a smooth, successful startup.',
  'A systematic approach to getting your building into move-in condition is what you\u2019re looking for. So, at {{companyName}}, we systematically clear the area of our equipment and supplies and return them to their proper place, so while our results are noticeable, the process is not.',
]

// ---------------------------------------------------------------------------
// Template resolution
// ---------------------------------------------------------------------------

function familyForServiceType(serviceType: string): string {
  switch (serviceType) {
    case 'maintenance':
    case 'maintenance_dsny':
      return 'maintenance'
    case 'maintenance_prevailing_wage':
    case 'dsny_prevailing_wage':
      return 'prevailing_wage'
    case 'construction_cleaning':
      return 'construction'
    case 'apartment_turnovers':
      return 'turnover'
    case 'office_cleaning':
      return 'office'
    case 'hourly_clean':
      return 'hourly'
    case 'cleaning':
      return 'construction'
    default:
      return 'custom'
  }
}

export function getDocumentTemplate(serviceType: string | null | undefined): DocumentTemplate {
  const st = (serviceType || '').trim()
  const family = familyForServiceType(st)
  const dsny = st === 'maintenance_dsny' || st === 'dsny_prevailing_wage'

  if (family === 'maintenance') {
    return {
      family,
      documentTitle: 'Building Maintenance Proposal',
      bodyTitle: 'Professional Building Upkeep Proposal',
      subject: 'Building Maintenance \u2013 Janitorial Services',
      introParagraphs: MAINTENANCE_INTRO,
      sections: maintenanceSections({ dsny }),
      defaultWorkSpecs: MAINTENANCE_WORK_SPECS,
      defaultDocumentData: {
        suppliesBy: 'company',
        equipmentBy: 'company',
        snow: { enabled: true, thresholdInches: 8, overRate: 150, underRate: 95, includedCount: 0 },
        additionalWork: { rate: 40, unit: 'per_hour' },
        autoRenew: true,
        pricingMode: 'total',
      },
    }
  }

  if (family === 'prevailing_wage') {
    return {
      family,
      documentTitle: 'Building Maintenance Proposal',
      bodyTitle: 'Professional Building Upkeep Proposal',
      subject: 'Building Maintenance \u2013 Janitorial Services (Prevailing Wage)',
      introParagraphs: MAINTENANCE_INTRO,
      sections: prevailingWageSections({ dsny }),
      defaultWorkSpecs: MAINTENANCE_WORK_SPECS,
      defaultDocumentData: {
        suppliesBy: 'customer',
        equipmentBy: 'customer',
        snow: { enabled: true, thresholdInches: 8, overRate: 150, underRate: 95, includedCount: 0 },
        additionalWork: { rate: 44.51, unit: 'per_hour' },
        autoRenew: true,
        pricingMode: 'total',
        pw: { rateExpiryDate: null, ptoHolidayBilledSeparately: true },
      },
    }
  }

  if (family === 'turnover') {
    return {
      family,
      documentTitle: 'Apartment Turnover Cleaning Proposal',
      bodyTitle: 'Professional Apartment Turnover Proposal',
      subject: 'Apartment Turnover Cleaning',
      introParagraphs: CLEANING_INTRO,
      sections: cleaningSections({ workLabel: 'apartment turnover cleaning' }),
      defaultWorkSpecs: CLEANING_SPECS,
      defaultDocumentData: {
        suppliesBy: 'company',
        equipmentBy: 'company',
        additionalWork: { rate: 39, unit: 'per_hour' },
        pricingMode: 'rate_card',
      },
    }
  }

  if (family === 'office') {
    return {
      family,
      documentTitle: 'Office Cleaning Proposal',
      bodyTitle: 'Professional Office Cleaning Proposal',
      subject: 'Office Cleaning Services',
      introParagraphs: CLEANING_INTRO,
      sections: cleaningSections({ workLabel: 'office cleaning' }),
      defaultWorkSpecs: OFFICE_WORK_SPECS,
      defaultDocumentData: {
        suppliesBy: 'company',
        equipmentBy: 'company',
        additionalWork: { rate: 39, unit: 'per_hour' },
        pricingMode: 'total',
      },
    }
  }

  if (family === 'hourly') {
    return {
      family,
      documentTitle: 'Hourly Cleaning Proposal',
      bodyTitle: 'Professional Cleaning Proposal',
      subject: 'Cleaning Services',
      introParagraphs: CLEANING_INTRO,
      sections: cleaningSections({ workLabel: 'cleaning' }),
      defaultWorkSpecs: CLEANING_SPECS,
      defaultDocumentData: {
        suppliesBy: 'company',
        equipmentBy: 'company',
        additionalWork: { rate: 39, unit: 'per_hour' },
        crew: { size: 5, rate: 195 },
        pricingMode: 'total',
      },
    }
  }

  if (family === 'custom') {
    return {
      family,
      documentTitle: 'Service Proposal',
      bodyTitle: 'Professional Service Proposal',
      subject: 'Services',
      introParagraphs: CLEANING_INTRO,
      sections: cleaningSections({ workLabel: 'service' }),
      defaultWorkSpecs: [],
      defaultDocumentData: {
        suppliesBy: 'company',
        equipmentBy: 'company',
        pricingMode: 'total',
      },
    }
  }

  // construction (and legacy cleaning)
  return {
    family: 'construction',
    documentTitle: 'Post Construction Cleaning Proposal',
    bodyTitle: 'Professional Construction Cleaning Proposal',
    subject: 'Post Construction Cleaning',
    introParagraphs: CLEANING_INTRO,
    sections: cleaningSections({ workLabel: 'construction cleaning' }),
    defaultWorkSpecs: CLEANING_SPECS,
    defaultDocumentData: {
      suppliesBy: 'company',
      equipmentBy: 'company',
      additionalWork: { rate: 39, unit: 'per_hour' },
      pricingMode: 'total',
    },
  }
}

/**
 * Build the initial editable `document_data` for a service type, seeding the
 * section list (all included by default) plus the template's default values
 * and work specs. Used by the forms to prefill on Service Type change.
 */
export function buildDefaultDocumentData(serviceType: string | null | undefined): DocumentData {
  const tmpl = getDocumentTemplate(serviceType)
  const sections: DocumentSectionOverride[] = tmpl.sections.map((s) => ({
    id: s.id,
    included: true,
  }))
  return {
    ...tmpl.defaultDocumentData,
    sections,
    workSpecs: tmpl.defaultWorkSpecs.map((w) => ({ ...w })),
  }
}

/** Replace {{token}} placeholders in a string from a token map. Unknown tokens are removed. */
export function interpolate(text: string, tokens: Record<string, string>): string {
  if (!text) return ''
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, key) =>
    Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : ''
  )
}
